import React, { useState, useEffect } from 'react';
import { Activity, Search, Filter, Calendar, User, FileText } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Mapeamento de ações para exibição amigável
const actionLabels = {
  'TENANT_CREATED': { label: 'Empresa Criada', color: 'bg-green-100 text-green-700' },
  'TENANT_UPDATED': { label: 'Empresa Atualizada', color: 'bg-blue-100 text-blue-700' },
  'TENANT_SUSPENDED': { label: 'Empresa Suspensa', color: 'bg-red-100 text-red-700' },
  'TENANT_ACTIVATED': { label: 'Empresa Ativada', color: 'bg-green-100 text-green-700' },
  'USER_CREATED': { label: 'Usuário Criado', color: 'bg-purple-100 text-purple-700' },
  'USER_UPDATED': { label: 'Usuário Atualizado', color: 'bg-purple-100 text-purple-700' },
  'USER_DELETED': { label: 'Usuário Excluído', color: 'bg-red-100 text-red-700' },
  'TEMPLATE_CREATED': { label: 'Template Criado', color: 'bg-orange-100 text-orange-700' },
  'SECTOR_CREATED': { label: 'Setor Criado', color: 'bg-cyan-100 text-cyan-700' }
};

function MasterAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    search: ''
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(filters).toString();
      const res = await axios.get(`${API}/api/master-admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar logs:', err);
      setLoading(false);
    }
  };

  const getActionInfo = (action) => {
    return actionLabels[action] || { label: action, color: 'bg-gray-100 text-gray-700' };
  };

  const formatDetails = (details) => {
    if (!details) return '—';
    try {
      const obj = typeof details === 'string' ? JSON.parse(details) : details;
      return Object.entries(obj).map(([key, value]) => (
        <div key={key} className="mb-1">
          <span className="font-medium text-gray-700">{key}:</span>{' '}
          <span className="text-gray-600">{String(value)}</span>
        </div>
      ));
    } catch {
      return '—';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Activity size={28} className="text-orange-600" />
          Logs de Auditoria
        </h1>
        <p className="text-gray-500 mt-1">Acompanhe todas as ações realizadas no sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.search}
              onChange={e => setFilters({...filters, search: e.target.value})}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
            />
          </div>
          <select
            value={filters.action}
            onChange={e => setFilters({...filters, action: e.target.value})}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
          >
            <option value="">Todas as ações</option>
            {Object.entries(actionLabels).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filters.entity_type}
            onChange={e => setFilters({...filters, entity_type: e.target.value})}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none transition-all"
          >
            <option value="">Todos os tipos</option>
            <option value="tenant">Empresa</option>
            <option value="user">Usuário</option>
            <option value="template">Template</option>
            <option value="sector">Setor</option>
            <option value="conversation">Conversa</option>
          </select>
        </div>
      </div>

      {/* Lista de Logs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-4">Ação</th>
                <th className="px-6 py-4">Entidade</th>
                <th className="px-6 py-4">Realizado por</th>
                <th className="px-6 py-4">Data/Hora</th>
                <th className="px-6 py-4">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                      <span>Carregando logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                    <Activity size={48} className="mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium">Nenhum log encontrado</p>
                    <p className="text-sm mt-1">As ações realizadas aparecerão aqui</p>
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => {
                  const actionInfo = getActionInfo(log.action);
                  return (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-bold ${actionInfo.color}`}>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">{log.entity_type}</p>
                            <p className="text-xs text-gray-500">ID: {log.entity_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {log.performed_by_name || `ID: ${log.performed_by}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={14} className="text-gray-400" />
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs space-y-1 bg-gray-50 p-3 rounded-lg max-w-xs">
                          {formatDetails(log.details)}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity size={20} className="text-orange-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Total de Logs</p>
              <p className="text-xs text-gray-600">Últimas 100 ações registradas</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-orange-600">{logs.length}</p>
        </div>
      </div>
    </div>
  );
}

export default MasterAuditLogs;